import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { InviteSlot } from '@/types';

interface DraftInvitationsProps {
  slots: InviteSlot[];
}

type SlotState =
  | 'revoked'
  | 'pending'
  | 'joined_incomplete'
  | 'joined_complete';

function getSlotState(slot: InviteSlot): SlotState {
  if (slot.revokedAt) return 'revoked';
  if (!slot.claimedByProfileId) return 'pending';
  return slot.claimerCompletedAt ? 'joined_complete' : 'joined_incomplete';
}

const stateBadge: Record<SlotState, { label: string; className: string }> = {
  revoked: {
    label: 'Revoked',
    className: 'bg-slate-100 text-slate-500 border-slate-200',
  },
  pending: {
    label: 'Pending',
    className: 'bg-amber-100 text-amber-800 border-amber-300',
  },
  joined_incomplete: {
    label: 'Joined · profile incomplete',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  },
  joined_complete: {
    label: 'Joined · complete',
    className: 'bg-green-100 text-green-800 border-green-300',
  },
};

export function DraftInvitations({ slots }: DraftInvitationsProps) {
  const active = slots.filter((s) => !s.revokedAt);
  const joined = active.filter((s) => s.claimedByProfileId);
  const completed = joined.filter((s) => s.claimerCompletedAt);
  const pending = active.length - joined.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invitations</CardTitle>
        <CardDescription>
          Invite slots created by the leader. The leader is not listed here —
          they always count as one extra member.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-muted-foreground text-sm">
          {active.length} active invitation{active.length === 1 ? '' : 's'} ·{' '}
          {joined.length} joined · {completed.length} completed · {pending}{' '}
          missing
        </div>

        {slots.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No invitations created yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {slots.map((slot) => {
              const state = getSlotState(slot);
              const badge = stateBadge[state];
              return (
                <li
                  key={slot.id}
                  className="flex items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-sm">
                      {slot.claimerName ?? slot.placeholderName}
                    </div>
                    {slot.claimerName && (
                      <div className="truncate text-muted-foreground text-xs">
                        invited as “{slot.placeholderName}”
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className={badge.className}>
                    {badge.label}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
